import { useState, useEffect } from 'react';
import { Card, CardContent } from './ui/card';
import { ImageWithFallback } from './figma/ImageWithFallback';
import { motion } from 'framer-motion';
import {
  Users,
  Award,
  Target,
  Microscope,
  Brain,
  LocateFixed,
  BadgeCheck,
} from 'lucide-react';
import aboutBg from '../assets/about-bg.jpg';
import logo from '../assets/logo.png';

interface AboutPageProps {
  t: any;
  language: string;
}

export default function AboutPage({ t }: AboutPageProps) {
  const [visibleItems, setVisibleItems] = useState(new Set());

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisibleItems((prev) => new Set([...prev, entry.target.id]));
          }
        });
      },
      { threshold: 0.3 }
    );

    const timelineItems = document.querySelectorAll('.timeline-item');
    timelineItems.forEach((item) => observer.observe(item));

    return () => observer.disconnect();
  }, []);

  const timelineData = [
    { year: '1988', ...t.timeline['1988'], icon: Target },
    { year: '1999', ...t.timeline['1999'], icon: Microscope },
    { year: '2007', ...t.timeline['2007'], icon: Brain },
    { year: '2017', ...t.timeline['2017'], icon: LocateFixed },
    { year: '2021', ...t.timeline['2021'], icon: BadgeCheck },
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-b from-[#1A1A1A] to-gray-900 relative h-screen flex items-center justify-center overflow-hidden">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="absolute inset-0 z-0">
            <ImageWithFallback
              src={aboutBg}
              alt="Happy child playing with gamepad"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-black opacity-70"></div>
          </div>
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-4xl md:text-5xl font-bold mb-8 text-emerald-600 relative z-10"
          >
            {t.heroTitle}
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-lg text-gray-300 leading-relaxed relative z-10"
          >
            {t.heroDescription}
          </motion.p>
        </div>
      </section>

      {/* Research Image Section */}
      <section className="py-16 bg-gray-900">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <ImageWithFallback
                src={logo}
                alt="Research team working"
                className="w-full h-auto rounded-lg shadow-2xl"
              />
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="space-y-6"
            >
              <div className="flex items-center space-x-4">
                <Users className="h-8 w-8 text-emerald-600" />
                <h3 className="text-xl font-bold text-white">{t.researchFirst}</h3>
              </div>
              <div className="flex items-center space-x-4">
                <Award className="h-8 w-8 text-emerald-600" />
                <h3 className="text-xl font-bold text-white">{t.researchSecond}</h3>
              </div>
              <div className="flex items-center space-x-4">
                <Target className="h-8 w-8 text-emerald-600" />
                <h3 className="text-xl font-bold text-white">{t.researchThird}</h3>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-[#1A1A1A]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-3xl md:text-4xl font-bold text-center mb-4 text-emerald-600"
          >
            {t.ourMission}
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-lg text-center text-gray-300 leading-relaxed relative z-10"
          >
            {t.heroDescription}
          </motion.p>
          <motion.div
            className="relative m-2 flex items-start space-x-6"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            {/* Content */}
            <Card className="flex-1 bg-gray-800 border-gray-700 hover:border-emerald-500 transition-colors">
              <CardContent className="p-6">
                <p className="text-gray-300 text-center">{t.heroSecondDescription}</p>
              </CardContent>
            </Card>
          </motion.div>
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-3xl md:text-4xl font-bold text-center mt-10 mb-4 text-emerald-600"
          >
            {t.aboutCompany}
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-lg text-start text-gray-300 leading-relaxed relative z-10 whitespace-pre-line "
            dangerouslySetInnerHTML={{ __html: t.aboutBigText }}
          />
        </div>
      </section>

      {/* Timeline Section */}
      <section className="py-20 bg-gray-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-3xl md:text-4xl font-bold text-center mb-16 text-emerald-600"
          >
            {t.historyTitle}
          </motion.h2>

          <div className="relative">
            {/* Timeline line */}
            <div className="absolute left-8 top-0 bottom-0 w-0.5 bg-emerald-800"></div>

            <div className="space-y-12">
              {timelineData.map((item, index) => {
                const Icon = item.icon;
                const isVisible = visibleItems.has(`timeline-${index}`);

                return (
                  <motion.div
                    key={item.year}
                    id={`timeline-${index}`}
                    className="timeline-item relative flex items-start space-x-6"
                    initial={{ opacity: 0, x: -50 }}
                    animate={isVisible ? { opacity: 1, x: 0 } : { opacity: 0, x: -50 }}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                  >
                    {/* Timeline dot */}
                    <div className="relative z-10 flex-shrink-0">
                      <div className="w-16 h-16 bg-emerald-800 rounded-full flex items-center justify-center shadow-lg">
                        <Icon className="h-8 w-8 text-white" />
                      </div>
                    </div>

                    {/* Content */}
                    <Card className="flex-1 bg-gray-800 border-gray-700 hover:border-emerald-500 transition-colors">
                      <CardContent className="p-6">
                        <div className="flex items-center space-x-4 mb-3">
                          <span className="text-2xl font-bold text-emerald-600">{item.year}</span>
                          {/* <h3 className="text-xl font-bold text-white">{item.title}</h3> */}
                        </div>
                        <p className="text-gray-300">{item.description}</p>
                      </CardContent>
                    </Card>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
